import { AddOutlined, RefreshOutlined } from "@mui/icons-material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { TYPE_NOTIFICATION } from "../../../components/reusable/NotificationBull";
import useConfirm from "../../../hooks/useConfirm";
import { useQuery } from "../../../hooks/useQuery";
import useToast from "../../../hooks/useToast";
import { DomaineParAMIService } from "../../../services/candidature/DomaineParAMIService";
import AssignerDomaineList from "./domaine_sousdomaine/AssignerDomaineList";
import SousDomaineForm from "./SousDomaineForm";

const DomaineSousDomainePage = forwardRef((props, ref) => {
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const { Toast, showToast } = useToast();
  const { DialogComponent, openConfirmDialog } = useConfirm();
  const [domaines, setDomaines] = useState([]);
  const url = useQuery();
  const idType = url.get("id_type");
  const showToastRef = useRef(showToast);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  const handleFetchDomaines = useCallback(
    (filtre) => {
      setFormLoading(true);
      DomaineParAMIService.lister(filtre)
        .then((response) => {
          if (response && response.data) {
            setDomaines(response.data);
          }
        })
        .catch((error) => {
          showToastRef.current(
            TYPE_NOTIFICATION.ERROR,
            "Erreur lors de la récupération des domaines"
          );
          console.log(error);
        })
        .finally(() => {
          setFormLoading(false);
        });
    },
    []
  );

  const handleRefresh = useCallback(() => {
    handleFetchDomaines({ id_type: idType });
  }, [handleFetchDomaines, idType]);

  const handleOpenForm = () => {
    setOpenFormDialog(true);
  };

  const handleCloseForm = () => {
    setOpenFormDialog(false);
    handleRefresh();
  };

  const confirmDeleteDomaine = (idDomaineType) => {
    showToast(TYPE_NOTIFICATION.INFO, "Suppression en cours...");
    DomaineParAMIService.delete({ id_domaine_type: idDomaineType })
      .then((res) => {
        if (res && res.success) {
          setDomaines((prevDomaines) =>
            prevDomaines.filter((d) => d.id_domaine_type !== idDomaineType)
          );
          showToast(TYPE_NOTIFICATION.SUCCESS, "Domaine supprimé avec succès");
        }
      })
      .catch((error) => {
        showToast(
          TYPE_NOTIFICATION.ERROR,
          "Erreur lors de la suppression du domaine"
        );
        console.log(error);
      });
  };

  const handleDeleteDomaine = (idDomaine) => {
    openConfirmDialog(
      "Confirmation de suppression",
      "Êtes-vous sûr de vouloir supprimer ce domaine ?",
      () => confirmDeleteDomaine(idDomaine)
    );
  };

  useImperativeHandle(ref, () => ({
    save: async () => true,
  }));

  useEffect(() => {
    handleFetchDomaines({ id_type: idType });
  }, [handleFetchDomaines, idType]);

  const totalSousDomaines = domaines.reduce(
    (total, domaine) => total + (domaine?.sous_domaines?.length || 0),
    0
  );

  return (
    <>
      <DialogComponent />
      <AssignerDomaineList onFormClose={handleCloseForm} open={openFormDialog} />
      <Box sx={{ width: "100%", p: 2 }}>
        <Toast />
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "primary.main",
                    backgroundColor: "#EAF2FF",
                    flexShrink: 0,
                  }}
                >
                  <FolderOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="overline" color="primary" fontWeight={700}>
                    Classification
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    Domaine et Sous-Domaine
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Organisez l'AMI par domaines de compétence et sous-domaines.
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  label={`${domaines.length} domaine${domaines.length > 1 ? "s" : ""}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  label={`${totalSousDomaines} sous-domaine${
                    totalSousDomaines > 1 ? "s" : ""
                  }`}
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
                <Tooltip title="Actualiser la liste des domaines">
                  <span>
                    <IconButton color="primary" onClick={handleRefresh} disabled={formLoading}>
                      <RefreshOutlined />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<AddOutlined />}
                  onClick={handleOpenForm}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Ajouter
                </Button>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2.5 }} />

            {formLoading && (
              <Stack spacing={1.5}>
                {[1, 2, 3].map((item) => (
                  <Skeleton key={item} variant="rectangular" height={76} sx={{ borderRadius: 2 }} />
                ))}
              </Stack>
            )}

            {!formLoading && domaines.length === 0 && (
              <Alert
                severity="warning"
                variant="outlined"
                action={
                  <Button
                    color="primary"
                    size="small"
                    startIcon={<AddOutlined />}
                    onClick={handleOpenForm}
                  >
                    Ajouter
                  </Button>
                }
                sx={{ borderRadius: 2, backgroundColor: "#FFFFFF", alignItems: "center" }}
              >
                Aucun domaine n'est encore assigné. Cette étape est facultative,
                mais elle permet de mieux catégoriser l'AMI.
              </Alert>
            )}

            {!formLoading && domaines.length > 0 && (
              <Grid container spacing={1.5}>
                {domaines.map((domaine) => (
                  <Grid item xs={12} key={domaine.id_domaine_type}>
                    <SousDomaineForm domaine={domaine} onDelete={handleDeleteDomaine} />
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
});

export default DomaineSousDomainePage;
